import { ApolloError, gql, useQuery } from "@apollo/client";
import { ShipmentStopType } from "@api/graphql/generated/generated-types";
import { getUserFacingErrorMessage } from "@src/common/lib/NetworkErrorHandling";

const FORCE_ATTEMPT_MODAL_SHIPMENT_QUERY = gql`
  query ForceAttemptModalShipment($courierId: ID!, $shipmentId: ID!) {
    shipmentById(courierId: $courierId, shipmentId: $shipmentId) {
      id
      stops {
        id
        type
        completedAt
        dispatchZone
        streetAddress
        city
        companyName
        tasks {
          sequenceNumber
        }
      }
    }
  }
`;

export interface ForceAttemptModalStop {
  id: string;
  type: ShipmentStopType;
  completedAt?: number | null;
  dispatchZone?: string | null;
  streetAddress?: string | null;
  city?: string | null;
  companyName?: string | null;
  tasks?: Array<{
    sequenceNumber: number;
  }> | null;
}

export interface ForceAttemptModalShipment {
  id: string;
  stops: ForceAttemptModalStop[];
}

interface ForceAttemptModalShipmentQueryData {
  shipmentById: ForceAttemptModalShipment | null;
}

interface ForceAttemptModalShipmentQueryVariables {
  courierId: string;
  shipmentId: string;
}

interface UseForceAttemptModalShipmentOptions {
  courierId: string | undefined;
  shipmentId: string;
  skip?: boolean;
}

interface UseForceAttemptModalShipmentResult {
  shipment: ForceAttemptModalShipment | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  // See useExceptionReasonOptions: the app's standard translated copy for this
  // failure when it has one, so a permission denial does not read as a generic
  // "could not load order" with a useless retry hint. undefined for a
  // network-only failure.
  errorMessage?: string;
}

export function useForceAttemptModalShipment({
  courierId,
  shipmentId,
  skip = false,
}: UseForceAttemptModalShipmentOptions): UseForceAttemptModalShipmentResult {
  const { data, loading, error } = useQuery<
    ForceAttemptModalShipmentQueryData,
    ForceAttemptModalShipmentQueryVariables
  >(FORCE_ATTEMPT_MODAL_SHIPMENT_QUERY, {
    variables: { courierId: courierId ?? "", shipmentId },
    skip: skip || !courierId || !shipmentId,
    fetchPolicy: "network-only",
  });

  return {
    shipment: data?.shipmentById ?? undefined,
    loading,
    error,
    errorMessage: getUserFacingErrorMessage(error),
  };
}
